import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AddIcon from '@mui/icons-material/Add';
import CameraSettingsRow from './CameraSettingsRow';
import AddCameraDialog from './AddCameraDialog';
import {
  getAllCameras,
  updateCamera,
  getSnapshotUrl,
  getCustomers,
  deleteCamera,
} from 'api/canvms';

function CameraSettings({ open, onClose, siteId, siteName, onCamerasChanged }) {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({}); // { cameraId: { display_name, enabled } }
  const [snapshots, setSnapshots] = useState({}); // { cameraId: url }
  const [snapshotsLoading, setSnapshotsLoading] = useState({});
  const [notification, setNotification] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // camera to delete
  const [deleting, setDeleting] = useState(false);

  const fetchCameras = useCallback(async () => {
    if (!siteId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getAllCameras(siteId);
      setCameras(response.data?.cameras || []);
      setChanges({});
    } catch (err) {
      console.error('[CANVMS/CameraSettings] Error fetching cameras:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch cameras');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const fetchSnapshots = useCallback(async () => {
    if (!siteId || cameras.length === 0) return;

    // Fetch snapshots for each camera
    cameras.forEach(async (camera) => {
      try {
        setSnapshotsLoading((prev) => ({ ...prev, [camera.id]: true }));
        const response = await getSnapshotUrl(siteId, camera.id);
        setSnapshots((prev) => ({
          ...prev,
          [camera.id]: response.data?.snapshot_url,
        }));
      } catch (err) {
        // Silently fail for snapshot errors
        console.warn(`[CANVMS/CameraSettings] Failed to load snapshot for camera ${camera.id}`);
      } finally {
        setSnapshotsLoading((prev) => ({ ...prev, [camera.id]: false }));
      }
    });
  }, [siteId, cameras]);

  useEffect(() => {
    if (open) {
      fetchCameras();
      setCustomersLoading(true);
      getCustomers()
        .then((res) => setCustomers(res.data?.customers || []))
        .catch(() => setCustomers([]))
        .finally(() => setCustomersLoading(false));
    }
  }, [open, fetchCameras]);

  useEffect(() => {
    if (open && cameras.length > 0) {
      fetchSnapshots();
    }
  }, [open, cameras.length, fetchSnapshots]);

  const handleChange = (cameraId, newValues) => {
    const camera = cameras.find((c) => c.id === cameraId);
    if (!camera) return;

    const originalDisplayName = camera.display_name || '';
    const originalEnabled = camera.enabled;
    const originalCustomerId = camera.customer_id || null;
    const originalAddressId = camera.address_id || null;
    const originalAutoIncidentEnabled = camera.auto_incident_enabled || false;
    let originalIncidentTriggers = [];
    try {
      originalIncidentTriggers = camera.incident_triggers ? JSON.parse(camera.incident_triggers) : [];
    } catch {
      originalIncidentTriggers = [];
    }
    const originalDefaultSeverity = camera.default_severity || 'medium';
    const originalCooldownSeconds = camera.incident_cooldown_seconds || 60;
    const originalAutoDismissThreshold = camera.auto_dismiss_threshold ?? 0;

    // Check if values are different from original
    const triggersChanged = JSON.stringify(newValues.incident_triggers?.sort() || []) !==
      JSON.stringify(originalIncidentTriggers.sort());

    const isModified =
      newValues.display_name !== originalDisplayName ||
      newValues.enabled !== originalEnabled ||
      (newValues.customer_id || null) !== originalCustomerId ||
      (newValues.address_id || null) !== originalAddressId ||
      newValues.auto_incident_enabled !== originalAutoIncidentEnabled ||
      triggersChanged ||
      newValues.default_severity !== originalDefaultSeverity ||
      newValues.incident_cooldown_seconds !== originalCooldownSeconds ||
      (newValues.auto_dismiss_threshold ?? 0) !== originalAutoDismissThreshold;

    if (isModified) {
      setChanges((prev) => ({
        ...prev,
        [cameraId]: newValues,
      }));
    } else {
      // Remove from changes if back to original
      setChanges((prev) => {
        const next = { ...prev };
        delete next[cameraId];
        return next;
      });
    }
  };

  const handleSave = async () => {
    const changeIds = Object.keys(changes);
    if (changeIds.length === 0) return;

    try {
      setSaving(true);
      setError(null);

      // Save all changes
      const promises = changeIds.map((cameraId) =>
        updateCamera(siteId, parseInt(cameraId), changes[cameraId])
      );

      await Promise.all(promises);

      setNotification({
        message: `Updated ${changeIds.length} camera(s) successfully`,
        severity: 'success',
      });

      // Refresh cameras list
      await fetchCameras();
      onCamerasChanged?.();
    } catch (err) {
      console.error('[CANVMS/CameraSettings] Error saving changes:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkEnable = (enable) => {
    const newChanges = {};
    cameras.forEach((camera) => {
      if (camera.enabled !== enable) {
        let originalTriggers = [];
        try {
          originalTriggers = camera.incident_triggers ? JSON.parse(camera.incident_triggers) : [];
        } catch {
          originalTriggers = [];
        }
        newChanges[camera.id] = {
          display_name: changes[camera.id]?.display_name ?? camera.display_name,
          enabled: enable,
          auto_incident_enabled: changes[camera.id]?.auto_incident_enabled ?? camera.auto_incident_enabled ?? false,
          incident_triggers: changes[camera.id]?.incident_triggers ?? originalTriggers,
          default_severity: changes[camera.id]?.default_severity ?? camera.default_severity ?? 'medium',
          incident_cooldown_seconds: changes[camera.id]?.incident_cooldown_seconds ?? camera.incident_cooldown_seconds ?? 60,
          auto_dismiss_threshold: changes[camera.id]?.auto_dismiss_threshold ?? camera.auto_dismiss_threshold ?? 0,
        };
      }
    });
    setChanges((prev) => ({ ...prev, ...newChanges }));
  };

  const handleClose = () => {
    if (Object.keys(changes).length > 0) {
      // Could show confirmation dialog here
      // For now, just discard changes
    }
    setChanges({});
    setSnapshots({});
    onClose();
  };

  const handleDeleteCamera = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deleteCamera(siteId, deleteTarget.id);
      setDeleteTarget(null);
      setNotification({ message: `Camera "${deleteTarget.display_name || deleteTarget.name}" deleted`, severity: 'success' });
      fetchCameras();
      onCamerasChanged?.();
    } catch (err) {
      console.error('[CANVMS/CameraSettings] Error deleting camera:', err);
      setNotification({
        message: err.response?.data?.error?.message || 'Failed to delete camera',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: { xs: '100%', md: 800 }, maxWidth: '100%' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">Camera Settings</Typography>
              {siteName && (
                <Typography variant="body2" color="text.secondary">
                  {siteName}
                </Typography>
              )}
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCameras}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving...' : `Save Changes${hasChanges ? ` (${Object.keys(changes).length})` : ''}`}
            </Button>

            <IconButton onClick={handleClose} sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Bulk Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 2,
              bgcolor: 'grey.50',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Bulk Actions:
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => handleBulkEnable(true)}
            >
              Enable All
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityOffIcon />}
              onClick={() => handleBulkEnable(false)}
            >
              Disable All
            </Button>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Tooltip title="Pending VMS migration">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled
                >
                  Add Camera
                </Button>
              </span>
            </Tooltip>

            {hasChanges && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography variant="body2" color="primary">
                  {Object.keys(changes).length} unsaved change(s)
                </Typography>
              </>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : cameras.length === 0 ? (
              <Alert severity="info">
                No cameras found. Try syncing cameras from the Site Management panel.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 100 }}>Preview</TableCell>
                    <TableCell>Original Name</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell sx={{ width: 120 }}>Status</TableCell>
                    <TableCell sx={{ width: 80 }} align="center">Enabled</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cameras.map((camera) => (
                    <CameraSettingsRow
                      key={camera.id}
                      camera={camera}
                      snapshotUrl={snapshots[camera.id]}
                      snapshotLoading={snapshotsLoading[camera.id]}
                      onChange={handleChange}
                      modified={!!changes[camera.id]}
                      customers={customers}
                      customersLoading={customersLoading}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>
      </Drawer>

      <AddCameraDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        siteId={siteId}
        onCameraAdded={() => {
          setAddDialogOpen(false);
          fetchCameras();
          onCamerasChanged?.();
          setNotification({ message: 'Camera added successfully', severity: 'success' });
        }}
        customers={customers}
      />

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete Camera</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.display_name || deleteTarget?.name}"?
            This will remove the camera from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCamera}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notification && (
          <Alert
            onClose={() => setNotification(null)}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}

export default CameraSettings;
